import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type MutableRefObject,
} from 'react';
import type { MediaPlayerInstance } from '@vidstack/react';
import { canonicalizeLocale, type VideoTrack } from '@course-authoring/shared-types';
import {
  CAPTION_PREF_OFF,
  CAPTION_PREF_ON,
  getStoredCaptionPref,
  setStoredCaptionPref,
  subscribeCaptionPref,
  isCaptionKind,
  resolveDefaultCaptionTrackIndex,
  type CaptionPreference,
} from '../videoCaptionPreference';

export interface VideoCaptionDefault {
  /** Track index that should start showing, or -1 for none. */
  defaultCaptionTrackIndex: number;
  /** Imperatively shows the default track once the player is ready (call from onCanPlay). */
  applyDefaultCaptionTrack: () => void;
}

function captionScopeKey(playerInstanceKey: number, locale: string | undefined): string {
  return `${playerInstanceKey}:${canonicalizeLocale(locale)}`;
}

function getServerCaptionPref(): CaptionPreference {
  return null;
}

/**
 * Owns the caption-default lifecycle for a video player (NA-73 / NA-349):
 * resolve whether captions start on and which track to show (stored boolean
 * pref + learner locale + account flag), apply it against Vidstack's runtime
 * track list, and persist the learner's manual on/off choice.
 *
 * Re-applies when the selected locale changes or the player remounts
 * (`playerInstanceKey`, e.g. after `onEnded`). Programmatic re-application
 * must not persist — only user-originated (`isOriginTrusted`) changes are written.
 *
 * Two race conditions are handled deliberately:
 * - The account config can resolve AFTER the player registered its tracks, and
 *   Vidstack only honors the `<Track default>` attribute at registration time
 *   — so the default is also applied imperatively (from `onCanPlay`, plus a
 *   retry effect for the config-arrives-late ordering).
 * - Vidstack fires `mode-change` programmatically during startup; persisting
 *   those would record a spurious "off" before any track shows.
 */
export function useVideoCaptionDefault(
  playerRef: MutableRefObject<MediaPlayerInstance | null>,
  tracks: VideoTrack[] | undefined,
  options: {
    videoClosedCaption: boolean;
    currentLocale?: string;
    /** Remount counter for the underlying MediaPlayer; re-arms the track-list listener. */
    playerInstanceKey: number;
  },
): VideoCaptionDefault {
  const { videoClosedCaption, currentLocale, playerInstanceKey } = options;

  // Shared across every mounted renderer: a manual change in one video block
  // re-resolves the default in the others (same-tab `storage` events don't fire).
  const storedPref = useSyncExternalStore(
    subscribeCaptionPref,
    getStoredCaptionPref,
    getServerCaptionPref,
  );
  const defaultCaptionTrackIndex = useMemo(
    () =>
      resolveDefaultCaptionTrackIndex(tracks, {
        videoClosedCaption,
        storedPref,
        locale: currentLocale,
      }),
    [tracks, videoClosedCaption, storedPref, currentLocale]
  );

  const appliedDefaultCaptionRef = useRef<string | null>(null);
  // The learner's user-originated choice in THIS renderer, scoped to the
  // instance+locale it was made in. While the shared pref still matches it,
  // defaults must not override the learner's pick (e.g. a manually chosen
  // track the strict resolver would hide). A locale change, remount, or a
  // newer manual change from another co-mounted renderer invalidates it.
  const manualCaptionChoiceRef = useRef<{ scope: string; pref: CaptionPreference } | null>(null);
  const applyDefaultCaptionTrack = useCallback(() => {
    const scopeKey = captionScopeKey(playerInstanceKey, currentLocale);
    // The resolved index is part of the key so a late-arriving account config
    // (or any input change that moves the resolved default) re-applies instead
    // of hitting the applied-once guard (NA-73's config-arrives-late retry).
    const applyKey = `${scopeKey}:${defaultCaptionTrackIndex}`;
    if (appliedDefaultCaptionRef.current === applyKey) return;
    const manual = manualCaptionChoiceRef.current;
    if (manual && manual.scope === scopeKey && manual.pref === storedPref) return;
    const list = playerRef.current?.textTracks;
    if (!list?.toArray) return;
    const items = list.toArray();

    const hideShowingCaptions = () => {
      for (const item of items) {
        if (isCaptionKind(item.kind) && item.mode === 'showing') {
          item.mode = 'disabled';
        }
      }
    };

    if (defaultCaptionTrackIndex < 0) {
      const authoredCaptions = tracks?.some((t) => isCaptionKind(t.kind) && Boolean(t.src));
      const runtimeCaptions = items.filter((t) => isCaptionKind(t.kind));
      // Player track list is not populated yet — wait for onCanPlay so an
      // authored `default` cannot stick after we would have marked applied.
      if (authoredCaptions && runtimeCaptions.length === 0) return;
      hideShowingCaptions();
      appliedDefaultCaptionRef.current = applyKey;
      return;
    }

    const target = tracks?.[defaultCaptionTrackIndex];
    if (!target) return;
    const targetLang = canonicalizeLocale(target.language);
    const match = items.find(
      (t) => isCaptionKind(t.kind) && canonicalizeLocale(t.language) === targetLang,
    );
    if (!match) return;

    if (match.mode !== 'showing') {
      hideShowingCaptions();
      match.mode = 'showing';
    }
    appliedDefaultCaptionRef.current = applyKey;
  }, [defaultCaptionTrackIndex, tracks, playerRef, playerInstanceKey, currentLocale, storedPref]);

  // Covers the reverse race: the video was ready (onCanPlay already fired)
  // before the account config resolved the default index. Also re-applies on
  // locale change (including hide-when-no-match) and player remount.
  useEffect(() => {
    applyDefaultCaptionTrack();
  }, [applyDefaultCaptionTrack]);

  // Persist the learner's manual caption choice so it beats every default on
  // later loads. Thin, defensive glue over Vidstack's text-track list (a no-op
  // where the list/events are unavailable, e.g. tests).
  const handleCaptionModeChange = useCallback(
    (event: { isOriginTrusted?: boolean }) => {
      if (!event.isOriginTrusted) return;
      const textTracks = playerRef.current?.textTracks;
      if (!textTracks) return;
      const showing = textTracks
        .toArray()
        .find((track) => track.mode === 'showing' && isCaptionKind(track.kind));
      const next = showing ? CAPTION_PREF_ON : CAPTION_PREF_OFF;
      manualCaptionChoiceRef.current = {
        scope: captionScopeKey(playerInstanceKey, currentLocale),
        pref: next,
      };
      setStoredCaptionPref(next);
    },
    [playerRef, playerInstanceKey, currentLocale]
  );

  useEffect(() => {
    const textTracks = playerRef.current?.textTracks;
    if (!textTracks?.addEventListener) return;
    textTracks.addEventListener('mode-change', handleCaptionModeChange);
    return () => {
      textTracks.removeEventListener('mode-change', handleCaptionModeChange);
    };
  }, [handleCaptionModeChange, playerRef, playerInstanceKey]);

  return { defaultCaptionTrackIndex, applyDefaultCaptionTrack };
}
